import { paperStyles } from '@festina-life/ui/libs'
import cx from 'classnames'
import type { ComponentType, ReactNode } from 'react'

import { StackableTableRow } from './StackableTableRow'
import type { RowActions, StackableType, ValidStackableRatios } from './types'

export const DEFAULT_STEP_INDENTATION = 20

type Props<C, P> = {
  /** Current number of indentation steps for the table. */
  indentationStep: number
  /** Size in Px of each indentation step.*/
  stepSizePx?: number
  /**
   * Headers to be shown on each column of the table, undefined if no headers should be shown for the current level.
   * Should not be confused with regular table headers, since these headers are rendered above the table.
   */
  headers?: string[]
  /**
   * Width in Px of the first column of the table before applying the indentation. Indentations make the first column narrower
   * by stepSizePx * indentationStep.
   */
  mainColumnWidthPx?: number
  /** Child stacked tables. */
  children?: ReactNode
  /**
   * External classname set on each row
   */
  rowClassName?: string
  /**
   * Callback for when a child row is clicked on.
   */
  onChildClicked?: (element: StackableType<C>) => void
  /**
   * Callback for when a parent row is clicked on.
   */
  onParentClicked?: () => void
  /**
   * Component used for rendering a child row cell.
   */
  childCell?: ComponentType<{
    data: C
    column: number
    row?: number
  }>
  /**
   * Component used for rendering a parent row cell.
   */
  parentCell?: ComponentType<{
    data: P
    column: number
    row?: number
  }>
  /**
   * Parent being rendered on the first row of the table. Undefined if no parent exists.
   */
  parent?: StackableType<P>
  /**
   * Data being rendered.
   */
  data?: StackableType<C>[]
  /**
   * Column ratios used for rows with empty values. This will always contain one entry less than the headers array because
   * the first column uses a fixed size and therefore doesn't have a flex ratio.
   */
  emptyRowColumnRatios: ValidStackableRatios[]

  parentActions?: RowActions<P>
  childActions?: RowActions<C>
}

/** Implements a table that can be stacked inside other stackable tables, making up a table hierarchy, where child tables
 * can be collapsed ir order to hide their contents and allow users to configure their desired layout.
 */
export const StackableTable = <C, P>({
  indentationStep,
  headers,
  stepSizePx = DEFAULT_STEP_INDENTATION,
  mainColumnWidthPx = 350,
  onChildClicked,
  onParentClicked,
  rowClassName,
  children,
  data,
  childCell,
  parentCell,
  parent,
  emptyRowColumnRatios,
  parentActions,
  childActions,
}: Props<C, P>) => {
  const levelIndentationPx = indentationStep * stepSizePx
  const columnsForEmptyField = emptyRowColumnRatios.length + 1

  const isHeadersConsistentWithMainRowRatios = headers?.length
    ? headers.length === columnsForEmptyField
    : true

  if (!isHeadersConsistentWithMainRowRatios) {
    throw new Error(
      'An inconsistent data set was provided for the table. All rows should have the same amount of values',
    )
  }

  const hasParent = parent && parentCell
  const hasChildren = !!data?.length && childCell

  return (
    <>
      {!!headers?.length && (
        <table
          className="sticky top-0 z-1 mb-2 table-fixed"
          style={{
            marginLeft: `${levelIndentationPx}px`,
            width: `calc(100% - ${levelIndentationPx}px)`,
          }}
        >
          <thead className="flex">
            <tr
              className={cx(
                'flex w-full flex-row',
                paperStyles({ isFaded: false }),
              )}
            >
              {headers.map((header, index) => (
                <th
                  className={cx(
                    'text-center text-sm font-medium round-lg flex justify-center items-center',
                    {
                      'flex-1':
                        index > 0 && emptyRowColumnRatios[index - 1] === 1,
                      'flex-2':
                        index > 0 && emptyRowColumnRatios[index - 1] === 2,
                      'flex-4':
                        index > 0 && emptyRowColumnRatios[index - 1] === 4,
                      'flex-5':
                        index > 0 && emptyRowColumnRatios[index - 1] === 5,
                      'min-w-[150px]': index === 1,
                    },
                  )}
                  // oxlint-disable-next-line react/no-array-index-key
                  key={`header-${header}-${indentationStep}-${index}`}
                  style={
                    index === 0
                      ? {
                          width: `${
                            mainColumnWidthPx - stepSizePx * indentationStep
                          }px`,
                        }
                      : undefined
                  }
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      )}
      <table
        className="mb-4 table-fixed"
        style={{
          marginLeft: `${levelIndentationPx}px`,
          width: `calc(100% - ${levelIndentationPx}px)`,
        }}
      >
        <tbody className="border border-solid border-slate-300 dark:border-slate-500">
          {hasParent && (
            <StackableTableRow
              tableCell={parentCell}
              rowClassName={rowClassName}
              data={parent}
              onClick={onParentClicked}
              indentationStep={indentationStep}
              emptyRowColumnRatios={emptyRowColumnRatios}
              mainColumnWidthPx={mainColumnWidthPx}
              actions={parentActions}
            />
          )}
          {hasChildren &&
            data.map((rowContent, rowIndex) => (
              <StackableTableRow
                mainColumnWidthPx={mainColumnWidthPx}
                // oxlint-disable-next-line react/no-array-index-key
                key={rowContent.key(rowIndex)}
                rowClassName={rowClassName}
                tableCell={childCell}
                data={rowContent}
                indentationStep={indentationStep}
                rowIndex={hasParent || rowIndex >= 1 ? rowIndex : undefined}
                onClick={onChildClicked}
                emptyRowColumnRatios={emptyRowColumnRatios}
                actions={childActions}
              />
            ))}
        </tbody>
      </table>
      {children}
    </>
  )
}
